import { useCallback, useEffect, useMemo, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";

import Button from "@musicfy/components/Button";
import Dialog from "@musicfy/components/Dialog";
import Icon from "@musicfy/components/Icon";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const BANNER_COOKIE = "hasDimissedFreeTrialBanner";

const DiscountBanner = (): JSX.Element | null => {
  const { data } = useSession();
  const user = data?.user;
  const router = useRouter();
  const { startCheckoutFlow } = usePaddleContext();

  const { subscription, isLoading: isSubscriptionLoading } =
    useSubscriptionContext();
  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);

  const isNewUser = !!user
    ? Date.now() - new Date(user.createdAt).getTime() < ONE_DAY_IN_MS * 2
    : false;

  const showBanner = useMemo(() => {
    return (
      router.query.show_pricing === undefined &&
      isBannerOpen &&
      !hasDismissed &&
      isNewUser &&
      !subscription
    );
  }, [
    hasDismissed,
    isBannerOpen,
    isNewUser,
    router.query.show_pricing,
    subscription,
  ]);

  useEffect(() => {
    const _hasDimissedBanner = Cookies.get(BANNER_COOKIE) === "true";

    if (_hasDimissedBanner) {
      setHasDismissed(true);
    } else {
      setIsBannerOpen(true);
    }
  }, []);

  const handleClose = useCallback(() => {
    setHasDismissed(true);
    setIsBannerOpen(false);
    Cookies.set(BANNER_COOKIE, "true", { expires: 7 });
  }, []);

  if (!showBanner || isSubscriptionLoading) {
    return null;
  }

  return (
    <Dialog
      open={showBanner}
      onClose={handleClose}
      title="Limited Time Offer"
      TitleIcon={<AccessTimeIcon />}
      width="450px"
      primaryButton={
        <Button
          fullWidth
          onClick={() => {
            handleClose();
            startCheckoutFlow("free-trial-banner");
          }}
        >
          Upgrade Today
        </Button>
      }
    >
      <div className="font-bold text-28">Save 60% on your first month!</div>
      <div className="mt-4 text-white/90 font-semibold">
        Upgrade today to unlock
      </div>
      <div className="mt-6 grid lg:grid-cols-2 grid-cols-1 gap-4">
        <div className="flex items-center gap-3">
          <Icon name="rotation" className="w-5" />
          <div>Unlimited Generations</div>
        </div>
        <div className="flex items-center gap-3">
          <Icon name="sound" className="w-5" />
          <div>Premium Sound</div>
        </div>
        <div className="flex items-center gap-3">
          <Icon name="cube" className="w-5" />
          <div>Custom Voices</div>
        </div>
        <div className="flex items-center gap-3">
          <Icon name="rocket" className="w-5" />
          <div>Faster Speeds</div>
        </div>
      </div>
    </Dialog>
  );
};

export default DiscountBanner;
